import React from 'react'
import { Crown, Phone, MessageCircle, Star, CheckCircle2, Sparkles, TrendingUp, Users, Shield } from "lucide-react";
import { motion } from "motion/react";
const EnterpriseSolutionSection = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
    <div className="container px-4 mx-auto max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="overflow-hidden relative bg-white rounded-3xl border-2 border-red-100 shadow-2xl"
      >
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-50 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-red-50 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 bg-red-50/50"></div>

        <div className="relative px-6 py-16 sm:px-12 sm:py-20">
          {/* Header Section */}
          <div className="mb-12 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex gap-2 items-center px-4 py-2 mb-4 bg-red-50 rounded-full border border-red-200"
            >
              <Sparkles className="w-5 h-5 text-red-600" />
              <span className="text-sm font-semibold tracking-wider text-red-700 uppercase">Enterprise Solution</span>
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl md:text-5xl"
            >
              Need a Custom Enterprise Plan?
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mx-auto max-w-2xl text-lg text-slate-600 sm:text-xl"
            >
              Tailored solutions designed specifically for your business needs. Get flexible billing, dedicated support, and custom features.
            </motion.p>
          </div>

          {/* Main Content Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mx-auto max-w-5xl"
          >
            <div className="p-8 bg-gray-50 rounded-2xl border border-gray-200 shadow-xl sm:p-10">
              <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
                {/* Left Section - Plan Info */}
                <div className="flex-1">
                  <div className="flex gap-4 items-center mb-6">
                    <div className="flex justify-center items-center w-16 h-16 bg-red-50 rounded-xl border border-red-200 shadow-lg">
                      <Crown className="w-8 h-8 text-red-600" />
                    </div>
                    <div>
                      <h3 className="mb-2 text-2xl font-bold text-slate-900 sm:text-3xl">Enterprise Plan</h3>
                      <p className="text-sm text-slate-600 sm:text-base">Customized billing for your business</p>
                    </div>
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex gap-3 items-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
                      <div className="flex justify-center items-center w-10 h-10 bg-red-50 rounded-lg border border-red-100">
                        <CheckCircle2 className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Flexible Billing</p>
                        <p className="text-xs text-slate-600">Pay as you use</p>
                      </div>
                    </div>
                    <div className="flex gap-3 items-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
                      <div className="flex justify-center items-center w-10 h-10 bg-red-50 rounded-lg border border-red-100">
                        <Users className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Dedicated Support</p>
                        <p className="text-xs text-slate-600">24/7 assistance</p>
                      </div>
                    </div>
                    <div className="flex gap-3 items-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
                      <div className="flex justify-center items-center w-10 h-10 bg-red-50 rounded-lg border border-red-100">
                        <TrendingUp className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Custom Features</p>
                        <p className="text-xs text-slate-600">Tailored to your needs</p>
                      </div>
                    </div>
                    <div className="flex gap-3 items-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
                      <div className="flex justify-center items-center w-10 h-10 bg-red-50 rounded-lg border border-red-100">
                        <Shield className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Priority Access</p>
                        <p className="text-xs text-slate-600">Enterprise-grade service</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Section - Contact Card */}
                <div className="flex justify-center lg:justify-end">
                  <div className="p-6 w-full bg-white rounded-2xl border-2 border-red-100 shadow-2xl sm:w-auto">
                    <div className="mb-6 text-center">
                      <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-xl shadow-lg">
                        <Star className="w-8 h-8 text-white" />
                      </div>
                      <h4 className="mb-2 text-xl font-bold text-slate-900">Get Started</h4>
                      <p className="text-sm text-slate-600">Contact us to discuss your custom plan</p>
                    </div>

                    {/* Phone Number */}
                    <div className="flex gap-3 items-center p-4 mb-6 bg-red-50 rounded-xl border border-red-200">
                      <div className="flex justify-center items-center w-12 h-12 bg-red-100 rounded-lg">
                        <Phone className="w-6 h-6 text-red-700" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="mb-1 text-xs font-medium tracking-wide text-red-600 uppercase">Contact Us</p>
                        <p className="text-lg font-bold text-slate-900">+233 55 548 8305</p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3">
                      <motion.a
                        href="https://wa.me/233272747529"
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] text-white font-semibold rounded-xl shadow-lg transition-all hover:shadow-xl hover:bg-[#20BA5A]"
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span>WhatsApp Us</span>
                      </motion.a>
                      <motion.a
                        href="tel:+233272747529"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex gap-2 justify-center items-center px-6 py-3 font-semibold text-white bg-red-600 rounded-xl shadow-lg transition-all hover:shadow-xl hover:bg-red-700"
                      >
                        <Phone className="w-5 h-5" />
                        <span>Call Now</span>
                      </motion.a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bottom Benefits */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap gap-4 justify-center mt-10"
          >
            <div className="flex gap-2 items-center px-4 py-2 bg-red-50 rounded-full border border-red-200">
              <CheckCircle2 className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-slate-900">No Setup Fees</span>
            </div>
            <div className="flex gap-2 items-center px-4 py-2 bg-red-50 rounded-full border border-red-200">
              <CheckCircle2 className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-slate-900">Scalable Solutions</span>
            </div>
            <div className="flex gap-2 items-center px-4 py-2 bg-red-50 rounded-full border border-red-200">
              <CheckCircle2 className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-slate-900">Custom Integration</span>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  </section>
  )
}

export default EnterpriseSolutionSection